import { Component, ElementRef, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { StadiumName, StadiumNameApi } from '../../core/http/stadium-name.api';
import { GameService } from '../../core/state/game.service';
import { stadiumImage } from '../dashboard/stadium-image';

@Component({
  selector: 'app-stadium-name',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  templateUrl: './stadium-name.component.html',
})
export class StadiumNameComponent implements OnInit {
  private readonly api = inject(StadiumNameApi);
  private readonly game = inject(GameService);
  readonly current = signal<StadiumName | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);
  readonly editing = signal(false);
  @ViewChild('nameDialog') private nameDialog!: ElementRef<HTMLDialogElement>;
  draft = '';

  readonly stadiumStarsArray = [1, 2, 3, 4, 5];

  readonly stadiumDisplayName = computed<string>(() => {
    return (
      this.current()?.displayName ||
      this.game.stadiumData()?.stadiumName ||
      this.game.stadiumData()?.name ||
      this.game.userClub().stadium?.name ||
      'Estadio Municipal'
    );
  });

  readonly effectiveCapacity = computed<number>(() => {
    return (
      this.game.stadiumData()?.capacity ||
      this.game.userClub().stadium?.capacity ||
      15000
    );
  });

  readonly effectiveRating = computed<number>(() => {
    return (
      this.game.stadiumData()?.ratingLevel ||
      this.game.userClub().stadium?.ratingLevel ||
      1.5
    );
  });

  readonly effectiveFacilitiesRating = computed<number>(() => {
    return (
      this.game.stadiumData()?.facilitiesRatingLevel ||
      this.game.stadiumData()?.ratingLevel ||
      this.game.userClub().stadium?.facilitiesRatingLevel ||
      this.game.userClub().stadium?.ratingLevel ||
      1.5
    );
  });

  readonly effectiveStadiumImage = computed<string>(() => {
    return stadiumImage(this.effectiveFacilitiesRating(), this.effectiveCapacity());
  });

  readonly effectiveCategory = computed<string>(() => {
    const rating = this.effectiveFacilitiesRating();
    if (rating >= 5.0) return 'Instalaciones de élite';
    if (rating >= 4.5) return 'Instalaciones premium';
    if (rating >= 4.0) return 'Instalaciones modernas';
    if (rating >= 3.5) return 'Instalaciones avanzadas';
    if (rating >= 3.0) return 'Instalaciones profesionales';
    if (rating >= 2.5) return 'Instalaciones renovadas';
    if (rating >= 2.0) return 'Instalaciones sencillas';
    if (rating >= 1.5) return 'Instalaciones básicas mejoradas';
    return 'Instalaciones básicas';
  });

  openEditor(): void {
    const stadium = this.current();
    if (!stadium || this.loading() || this.saving()) return;
    this.draft = stadium.baseName;
    this.error.set(null);
    this.success.set(false);
    this.editing.set(true);
    this.nameDialog.nativeElement.showModal();
  }

  closeEditor(): void {
    if (this.saving() || this.loading()) return;
    this.nameDialog.nativeElement.close();
  }

  onDialogClosed(): void {
    this.editing.set(false);
    this.error.set(null);
    this.draft = this.current()?.baseName ?? '';
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    this.closeEditor();
  }

  get canSave(): boolean {
    const name = this.draft.trim();
    return this.game.canAction('renameStadium') && !!this.current() && !this.loading() && !this.saving() &&
      name.length > 0 && name.length <= 100 && !/[\u0000-\u001f\u007f-\u009f]/.test(name) &&
      (this.current()!.version === 0 || name !== this.current()!.baseName);
  }

  ngOnInit(): void { void this.load(); }

  async load(): Promise<void> {
    if (this.loading() || this.saving()) return;
    const clubId = this.game.userClub()?.id;
    if (!clubId) return;
    if (!this.game.stadiumData()) {
      void this.game.fetchClubStadium(clubId, false);
    }
    this.loading.set(true);
    this.error.set(null);
    this.success.set(false);
    try {
      const value = await this.api.get(clubId);
      this.current.set(value);
      this.draft = value.baseName;
    } catch {
      this.current.set(null);
      this.error.set('No se ha podido consultar el nombre del estadio. Inténtalo de nuevo.');
    } finally {
      this.loading.set(false);
    }
  }

  async save(): Promise<void> {
    if (!this.canSave) return;
    const clubId = this.game.userClub()?.id;
    if (!clubId) return;
    this.saving.set(true);
    this.error.set(null);
    this.success.set(false);
    try {
      const value = await this.api.rename(clubId, this.draft.trim(), this.current()!.version);
      this.current.set(value);
      this.draft = value.baseName;
      this.success.set(true);
      this.nameDialog.nativeElement.close();
      await Promise.allSettled([
        this.game.fetchClubEconomy(clubId, true),
        this.game.fetchClubStadium(clubId, true),
        this.game.fetchClubLedger(clubId),
        this.game.fetchEconomicSummary(clubId, true),
      ]);
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      this.error.set(status === 409
        ? 'No se ha podido guardar: el nombre ha cambiado, la cuenta está inactiva o no hay saldo suficiente. Actualiza el nombre y revisa el saldo antes de reintentarlo.'
        : status === 403
          ? 'Solo el propietario del club puede cambiar el nombre del estadio.'
          : 'No se ha podido confirmar el cambio. Actualiza el nombre para comprobar si se guardó antes de reintentarlo.');
    } finally {
      this.saving.set(false);
    }
  }
}
